import React, { useEffect, useState } from "react";
import Button from "../../Button/Button";
import PasswordInput from "../../PasswordInput/PasswordInput";
import TextInput from "../../TextInput/TextInput";
import { Controller, useForm } from "react-hook-form";
import PasswordStrengthBar, {
  PasswordStrengthBarProgress,
} from "../../../libs/PasswordStrengthBar";
import useUser from "../../../hooks/useUser";

import styles from "./styles.module.scss";

interface SignUpFormValues {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string
}

const SignUpForm = () => {
  const { isLoading, onSignUp } = useUser();
  const [passwordStrength, setPasswordStrength] =
    useState<PasswordStrengthBarProgress | null>(null);
  const [isError, setIsError] = useState(true);

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, dirtyFields },
  } = useForm<SignUpFormValues>({
    mode: "onChange",
  });

  const passwordStrengthCheck = watch("password");
  const passwordStrengthCheckConfirm = watch("confirmPassword");

  useEffect(() => {
    if (passwordStrengthCheck === passwordStrengthCheckConfirm) {
      setIsError(false);
    } else {
      setIsError(true);
    }
  }, [passwordStrengthCheck, passwordStrengthCheckConfirm]);

  const handlePasswordStrengthChange = (
    newStrength: PasswordStrengthBarProgress
  ) => {
    setPasswordStrength(newStrength);
  };

  const isSubmitButtonDisabled =
    isLoading ||
    passwordStrength === PasswordStrengthBarProgress.WEAK ||
    Object.keys(errors).length > 0 ||
    !dirtyFields.email ||
    !dirtyFields.password ||
    !dirtyFields.confirmPassword ||
    isError || !dirtyFields.firstName || !dirtyFields.lastName;

  return (
    <div>
      <div className={styles.or}>
        <span> OR </span>
      </div>
      <form
        onSubmit={handleSubmit((data: SignUpFormValues) => {
          if (Object.keys(errors).length !== 0) return null;
          const URL = process.env.REACT_APP_URL as string;
          onSignUp({
            first_name: data.firstName,
            last_name: data.lastName,
            country: "AM",
            phone: "+37411111111",
            email: data.email,
            password: data.password,
            redirect_url: URL,
          });
        })}
        className={styles.form}
      >
        <Controller
          name="firstName"
          control={control}
          rules={{
            required: "This field is required.",
          }}
          render={({ field }) => (
            <TextInput
              label="Name"
              placeholder="Name"
              error={errors.firstName && errors.firstName.message}
              onChange={(event) => field.onChange(event.target.value)}
            />
          )}
        />
        <Controller
          name="lastName"
          control={control}
          rules={{
            required: "This field is required.",
          }}
          render={({ field }) => (
            <TextInput
              label="Surname"
              placeholder="Surname"
              error={errors.lastName && errors.lastName.message}
              onChange={(event) => field.onChange(event.target.value)}
            />
          )}
        />
        <Controller
          name="email"
          control={control}
          rules={{
            required: "This field is required.",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "That’s an invalid e-mail",
            },
          }}
          render={({ field }) => (
            <TextInput
              label="Email"
              placeholder="example@example.com"
              error={errors.email && errors.email.message}
              onChange={(event) => field.onChange(event.target.value)}
            />
          )}
        />

        <Controller
          name="password"
          control={control}
          rules={{
            required: "This field is required",
          }}
          render={({ field }) => {
            return (
              <>
                <PasswordInput
                  label="Password"
                  onChange={(event) => field.onChange(event.target.value)}
                />
                {field.value && (
                  <PasswordStrengthBar
                    value={field.value}
                    onProgressChange={handlePasswordStrengthChange}
                  />
                )}
              </>
            );
          }}
        />

        <Controller
          name="confirmPassword"
          control={control}
          rules={{
            required: "This field is required",
            // minLength: {
            //   value: 8,
            //   message: "Password must contain at least 8 characters",
            // },
          }}
          render={({ field }) => {
            return (
              <PasswordInput
                label="Confirm Password"
                error={errors.confirmPassword && errors.confirmPassword.message}
                onChange={(event) => field.onChange(event.target.value)}
              />
            );
          }}
        />
        <p>
          By creating an account, you agree to the
          <span>Terms of Service.</span>
        </p>
        <Button disabled={isSubmitButtonDisabled} mode="filled" type="submit">
          Sign Up
        </Button>
      </form>
    </div>
  );
};

export default SignUpForm;
